import React, { useState, useRef, useEffect } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import * as XLSX from 'xlsx';
import moment from 'moment';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import Modal from 'react-bootstrap/Modal';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { RiRefreshLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';
import LeaseApiService from '../../services/apiService';
import {
    parseExcelDate, parseExcelNumber, toText, dateColumnFormatter,
    amountColumnFormatter, extractRows, toOption, isEnvelopeFailure, logImportGroupsFetch,
} from './importHelpers';

// Excel column order matches LeaseInvoiceTemplate.xlsx / SH_LeaseInvoiceImportRowVM exactly.
const IMPORT_COLUMN_ORDER = [
    'refNo', 'dueDate', 'propertyNo', 'propertyName', 'unitNo',
    'installmentDescription', 'customerNo', 'customerName',
    'amount', 'vatAmount', 'total', 'narration',
];
const NUMBER_FIELDS = ['amount', 'vatAmount', 'total'];
const DATE_FIELDS = ['dueDate'];

const LeaseInvoiceImport = () => {
    const { t } = useTranslation(['financePosting', 'common']);
    const apiService = new LeaseApiService();
    const fileInputRef = useRef();

    const [activeTab, setActiveTab] = useState(0);

    // ---- Import Data tab ----
    const [importRows, setImportRows] = useState([]);
    const [saving, setSaving] = useState(false);

    // ---- Voucher List tab ----
    const [searchByGroup, setSearchByGroup] = useState(false);
    const [fromDate, setFromDate] = useState(moment().startOf('month').toDate());
    const [toDate, setToDate] = useState(moment().endOf('month').toDate());
    const [voucherCreated, setVoucherCreated] = useState(false);
    const [importGroupOptions, setImportGroupOptions] = useState([]);
    const [selectedImportGroup, setSelectedImportGroup] = useState(null);
    const [voucherRows, setVoucherRows] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchImportGroups();
    }, []);

    const fetchImportGroups = async () => {
        try {
            const res = await apiService.get('LEASE_INVOICE_IMPORT_GROUPS');
            const groups = extractRows(res);
            const options = groups.map(toOption);
            logImportGroupsFetch('LeaseInvoiceImport', res, groups, options);
            setImportGroupOptions(options);
        } catch (error) {
            console.error('Error fetching import groups:', error);
            toast.error(error?.response?.data?.message || t('common:somethingWentWrong', 'Could not load import groups'));
        }
    };

    const columnDefs = IMPORT_COLUMN_ORDER.map((field) => ({
        headerName: t(`financePosting:invoiceColumns.${field}`),
        field,
        filter: true,
        floatingFilter: true,
        suppressMovable: true,
        width: field === 'propertyName' || field === 'customerName' || field === 'installmentDescription' || field === 'narration' ? 200 : 140,
        ...(DATE_FIELDS.includes(field) ? { valueFormatter: dateColumnFormatter } : {}),
        ...(NUMBER_FIELDS.includes(field) ? { valueFormatter: amountColumnFormatter } : {}),
    }));

    // ===================== Import Data tab =====================
    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
                const dataRows = rows.slice(1).filter((row) => row.some((cell) => cell !== '' && cell !== null && cell !== undefined));
                const parsed = dataRows.map((row, index) => ({
                    id: index,
                    refNo: toText(row[0]),
                    dueDate: parseExcelDate(row[1]),
                    propertyNo: toText(row[2]),
                    propertyName: toText(row[3]),
                    unitNo: toText(row[4]),
                    installmentDescription: toText(row[5]),
                    customerNo: toText(row[6]),
                    customerName: toText(row[7]),
                    amount: parseExcelNumber(row[8]),
                    vatAmount: parseExcelNumber(row[9]),
                    total: parseExcelNumber(row[10]),
                    narration: toText(row[11]),
                }));
                setImportRows(parsed);
            } catch (error) {
                console.error('Error parsing Excel file:', error);
                toast.error(t('common:somethingWentWrong', 'Could not read the Excel file'));
            } finally {
                e.target.value = '';
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleRefreshImport = () => {
        setImportRows([]);
    };

    const validateImportRows = () => {
        for (let i = 0; i < importRows.length; i++) {
            const row = importRows[i];
            if (!row.dueDate) {
                toast.warning(t('financePosting:validation.dueDateRequired', { row: i + 1 }));
                return false;
            }
        }
        return true;
    };

    const handleSave = async () => {
        if (!importRows.length) {
            toast.warning(t('financePosting:noRecordsToSave'));
            return;
        }
        if (!validateImportRows()) return;

        try {
            setSaving(true);
            // Body must match SH_LeaseInvoiceImportSaveVM exactly ({ rows: [...] }, additionalProperties: false).
            const body = {
                rows: importRows.map(({ id, dueDate, ...rest }) => ({
                    ...rest,
                    dueDate: dueDate ? moment(dueDate).toISOString() : null,
                })),
            };
            const res = await apiService.post('LEASE_INVOICE_IMPORT_SAVE', body);
            if (isEnvelopeFailure(res)) {
                toast.error(res?.data?.message || t('common:somethingWentWrong', 'Save failed'));
                return;
            }
            toast.success(res?.data?.message || t('financePosting:recordsSavedSuccessfully'));
            setImportRows([]);
        } catch (error) {
            console.error('Error saving lease invoice import rows:', error);
            const serverMessage = error?.response?.data?.message || error?.response?.data?.title;
            toast.error(serverMessage || t('common:somethingWentWrong', 'Save failed'));
        } finally {
            setSaving(false);
        }
    };

    // ===================== Voucher List tab =====================
    const handleSearchToggle = (checked) => {
        setSearchByGroup(checked);
        setSelectedImportGroup(null);
        setVoucherRows([]);
    };

    const handleSearch = async () => {
        if (searchByGroup && !selectedImportGroup) {
            toast.warning(t('financePosting:selectImportGroupToSearch'));
            return;
        }
        try {
            setSearching(true);
            // Body must match SH_LeaseInvoiceImportSearchFilterVM exactly.
            const body = {
                searchByGroup,
                importGroup: searchByGroup ? (selectedImportGroup?.value || null) : null,
                fromDate: !searchByGroup ? moment(fromDate).startOf('day').toISOString() : null,
                toDate: !searchByGroup ? moment(toDate).endOf('day').toISOString() : null,
                isPosted: !searchByGroup && voucherCreated,
            };
            const res = await apiService.post('LEASE_INVOICE_IMPORT_SEARCH', body);
            setVoucherRows(extractRows(res));
        } catch (error) {
            console.error('Error searching lease invoice import vouchers:', error);
            toast.error(error?.response?.data?.message || t('common:somethingWentWrong', 'Search failed'));
            setVoucherRows([]);
        } finally {
            setSearching(false);
        }
    };

    const handleRefreshVoucherList = () => {
        setSearchByGroup(false);
        setSelectedImportGroup(null);
        setVoucherCreated(false);
        setFromDate(moment().startOf('month').toDate());
        setToDate(moment().endOf('month').toDate());
        setVoucherRows([]);
        fetchImportGroups();
    };

    const handleDelete = async () => {
        if (!selectedImportGroup) {
            toast.warning(t('financePosting:selectImportGroupToDelete'));
            return;
        }
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            setDeleting(true);
            // {importGroup} is a path segment (DELETE .../delete/{importGroup}), not a body/query param.
            const res = await apiService.deleteParameter('LEASE_INVOICE_IMPORT_DELETE', encodeURIComponent(selectedImportGroup?.value ?? ''));
            if (isEnvelopeFailure(res)) {
                toast.error(res?.data?.message || t('common:somethingWentWrong', 'Delete failed'));
                return;
            }
            toast.success(res?.data?.message || t('financePosting:recordsDeletedSuccessfully'));
            setVoucherRows([]);
            fetchImportGroups();
        } catch (error) {
            console.error('Error deleting lease invoice import group:', error);
            toast.error(error?.response?.data?.message || t('common:somethingWentWrong', 'Delete failed'));
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    const handleExportExcel = () => {
        if (!voucherRows?.length) {
            toast.warning(t('financePosting:noDataToExport'));
            return;
        }
        const excelData = voucherRows.map((row) => {
            const out = {};
            IMPORT_COLUMN_ORDER.forEach((field) => {
                out[t(`financePosting:invoiceColumns.${field}`)] = row[field] ?? '';
            });
            return out;
        });
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Voucher List');
        XLSX.writeFile(workbook, 'Lease_Invoice_Voucher_List.xlsx');
    };

    return (
        <div className="p-3">
            <input
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <div className="border-box2 my-3 p-3" style={{ borderRadius: '8px' }}>
                <div className="nav nav-pills overslid d-flex gap-4">
                    <div className={`dashitems sidebar-text center ${activeTab === 0 ? 'sideActive' : ''}`} onClick={() => setActiveTab(0)}>
                        {t('financePosting:importDataTab')}
                    </div>
                    <div className={`dashitems sidebar-text center ${activeTab === 1 ? 'sideActive' : ''}`} onClick={() => setActiveTab(1)}>
                        {t('financePosting:voucherListTab')}
                    </div>
                </div>
            </div>

            {activeTab === 0 && (
                <div className="border-box2 p-3" style={{ borderRadius: '8px' }}>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="d-flex gap-2">
                            <button className="mainbtn" type="button" onClick={handleImportClick}>
                                {t('financePosting:excelImport')}
                            </button>
                            <button className="mainbtnGry" type="button" onClick={handleRefreshImport}>
                                {t('financePosting:refresh')}
                            </button>
                            {saving ? (
                                <button className="mainbtnSecondary" type="button" disabled>
                                    <ClipLoader color="#ffffff" size={18} />
                                </button>
                            ) : (
                                <button className="mainbtnSecondary" type="button" onClick={handleSave} disabled={!importRows.length}>
                                    {t('financePosting:save')}
                                </button>
                            )}
                        </div>
                        <div className="text-end">
                            <a href="/templates/LeaseInvoiceTemplate.xlsx" download="LeaseInvoiceTemplate.xlsx" className="d-block mb-1">
                                {t('financePosting:downloadTemplate')}
                            </a>
                            <div>
                                <span className="fw-bold me-1">{t('financePosting:totalRecords')}</span>
                                <span>{importRows.length}</span>
                            </div>
                        </div>
                    </div>
                    <div className="gridshad mt-3">
                        <div className="ag-theme-quartz" style={{ height: 500 }}>
                            <AgGridReact
                                getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
                                rowData={importRows}
                                enableFilter={true}
                                floatingFilter={true}
                                columnDefs={columnDefs}
                            />
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 1 && (
                <div className="border-box2 p-3" style={{ borderRadius: '8px' }}>
                    <div className="form-group grid-contain">
                        <div className="d-flex">
                            <label className="form-label pt-1 start">{t('common:from')}</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={(date) => setFromDate(date)}
                                dateFormat="dd-MMM-yyyy"
                                className="p-2 form-control w-100"
                                disabled={searchByGroup}
                            />
                        </div>
                        <div className="d-flex">
                            <label className="form-label pt-1 start">{t('common:to')}</label>
                            <DatePicker
                                selected={toDate}
                                onChange={(date) => setToDate(date)}
                                dateFormat="dd-MMM-yyyy"
                                className="p-2 form-control w-100"
                                disabled={searchByGroup}
                            />
                        </div>
                        <div className="d-flex gap-4 center">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={searchByGroup}
                                onChange={(e) => handleSearchToggle(e.target.checked)}
                                style={{ width: '20px', height: '20px' }}
                            />
                            <label>{t('financePosting:or')}</label>
                        </div>
                        <div className="d-flex">
                            <label className="form-label pt-1 start">{t('financePosting:importGroup')}</label>
                            <Select
                                className="p-0 w-100"
                                isDisabled={!searchByGroup}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                value={selectedImportGroup}
                                onChange={(option) => setSelectedImportGroup(option)}
                                options={importGroupOptions}
                                placeholder={t('financePosting:selectImportGroup')}
                                isClearable
                            />
                        </div>
                        <div className="d-flex gap-4 center">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={voucherCreated}
                                onChange={(e) => setVoucherCreated(e.target.checked)}
                                disabled={searchByGroup}
                                style={{ width: '20px', height: '20px' }}
                            />
                            <label>{t('financePosting:voucherCreated')}</label>
                        </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                        <div className="d-flex gap-2">
                            {searching ? (
                                <button className="mainbtnGry" type="button" disabled>
                                    <ClipLoader color="#ffffff" size={18} />
                                </button>
                            ) : (
                                <div className="mainbtnGry flex-end gap-2" style={{ cursor: 'pointer' }} onClick={handleSearch}>
                                    <FaFileCircleExclamation color="#fff" className="center mt-1" />
                                    <div className="center">{t('financePosting:search')}</div>
                                </div>
                            )}
                            <div className="mainbtnRed flex-end gap-2" style={{ cursor: 'pointer' }} onClick={handleRefreshVoucherList}>
                                <RiRefreshLine color="#fff" className="center mt-1" />
                                <div className="center">{t('financePosting:refresh')}</div>
                            </div>
                            <button className="mainbtnSecondary" type="button" onClick={handleDelete} disabled={!selectedImportGroup || !voucherRows.length}>
                                {t('financePosting:delete')}
                            </button>
                            <button className="mainbtn" type="button" onClick={handleExportExcel}>
                                {t('financePosting:exportToExcel')}
                            </button>
                        </div>
                        <div>
                            <span className="fw-bold me-1">{t('financePosting:totalRecords')}</span>
                            <span>{voucherRows.length}</span>
                        </div>
                    </div>
                    <div className="gridshad mt-3">
                        <div className="ag-theme-quartz" style={{ height: 500 }}>
                            <AgGridReact
                                getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: '#F0EDF5' } : { background: '#fff' })}
                                rowData={voucherRows}
                                enableFilter={true}
                                floatingFilter={true}
                                columnDefs={columnDefs}
                            />
                        </div>
                    </div>
                </div>
            )}

            <Modal show={showDeleteConfirm} size="md" onHide={() => setShowDeleteConfirm(false)} centered backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('financePosting:delete')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {t('financePosting:confirmDeleteGroup', { group: selectedImportGroup?.label })}
                </Modal.Body>
                <Modal.Footer className="end p-3">
                    <button className="mainbtnRed" onClick={() => setShowDeleteConfirm(false)}>{t('common:cancel')}</button>
                    {deleting ? (
                        <button className="mainbtn" disabled><ClipLoader color="#ffffff" size={18} /></button>
                    ) : (
                        <button className="mainbtn" onClick={confirmDelete}>{t('common:delete')}</button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default LeaseInvoiceImport;
